import {
  ICON_SIZES,
  NUGGET_CARD_WIDTH,
  PlaylistCreator,
  PriorityPlaylist,
} from '@backrs_ui/common-ui';

import { personInitial, personName } from '@backrs_ui/grow-ui/src/utils/personName';

import { Avatar, Box, Theme, SxProps, Typography } from '@mui/material';

import { PlaybookCardImage } from '../atoms/PlaybookCardImage';

export type PlaylistCardProps = Readonly<{
  id: string;
  playlist: PriorityPlaylist;
  onClick: () => void;
  opacity?: number;
}>;

export const PlaylistCard = ({ id, playlist, onClick, opacity = 1 }: PlaylistCardProps) => {
  const { createdBy } = playlist;

  return (
    <Box id={id} sx={containerSx(opacity)} onClick={onClick}>
      <Box sx={imageAreaSx}>
        <PlaybookCardImage
          playlist={playlist}
          badgeIcon={createdBy ? <CreatorAvatar creator={createdBy} /> : undefined}
        />
      </Box>

      <Typography variant="text-md-semibold" sx={titleSx}>
        {playlist.title}
      </Typography>
    </Box>
  );
};

const CreatorAvatar = ({ creator }: { creator: PlaylistCreator }) => {
  const initials = [creator.first_name, creator.last_name]
    .map((part) => personInitial({ first_name: part, last_name: null }))
    .join('');

  return (
    <Avatar src={creator.profile_image ?? undefined} alt={personName(creator)} sx={avatarSx}>
      {initials}
    </Avatar>
  );
};

const containerSx =
  (opacity: number): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: backrs.spacing['spacing-md'],
    width: '100%',
    maxWidth: NUGGET_CARD_WIDTH,
    cursor: 'pointer',
    userSelect: 'none',
    opacity,
  });

const imageAreaSx: SxProps<Theme> = ({ backrs }) => ({
  width: '100%',
  aspectRatio: `${NUGGET_CARD_WIDTH} / 180`,
  borderRadius: backrs.radius['radius-2xl'],
  overflow: 'hidden',
  outline: `1px solid ${backrs.colors['Colors/Base/Black']}`,
  transition: 'box-shadow 0.3s ease-in-out, transform 0.3s ease-in-out',
  '&:hover': {
    boxShadow: backrs.shadows['shadow-lg'],
    transform: 'translate(-2px, -2px)',
  },
});

const titleSx: SxProps<Theme> = ({ backrs }) => ({
  paddingLeft: backrs.spacing['spacing-xl'],
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

const avatarSx: SxProps<Theme> = ({ backrs, typography }) => ({
  ...typography['text-xs-semibold'],
  width: ICON_SIZES['16px'],
  height: ICON_SIZES['16px'],
  fontSize: '8px', // Hardcoded: no type token small enough for a 16px avatar
  backgroundColor: backrs.colors['Colors/Background/bg-tertiary'],
  color: backrs.colors['Colors/Foreground/fg-primary'],
});
